import { CollapsiblePaper, Page } from '@app-builder/components';
import { CreateFilter } from '@app-builder/components/Settings/Scenario/CreateFilter';
import { useLoaderRevalidator } from '@app-builder/contexts/LoaderRevalidatorContext';
import { authMiddleware } from '@app-builder/middlewares/auth-middleware';
import { ExportedFields } from '@app-builder/models';
import type { DataModel, Pivot } from '@app-builder/models/data-model';
import {
  type DeleteExportedFieldPayload,
  useDeleteFilterMutation,
} from '@app-builder/queries/settings/scenarios/delete-filter';
import { createFileRoute } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type { MarbleTableFeatures } from 'ui-design-system';
import { Button, Modal, Table, useTable } from 'ui-design-system';
import { Icon } from 'ui-icons';

type FilterRow = {
  id: string;
  tableId: string;
  associatedObject: string;
  definition: string;
  kind: 'trigger' | 'ingested';
  field?: string;
  path?: string[];
  name?: string;
};

type TriggerFieldItem = {
  tableId: string;
  tableName: string;
  fieldName: string;
  label: string;
};

type LinkPivotFieldItem = {
  baseTableId: string;
  pathLinks: string[];
  fieldName: string;
  label: string;
};

const getFilters = (exportedEntries: [string, ExportedFields][], dataModel: DataModel): FilterRow[] => {
  const exportedFieldsByTable = Object.fromEntries(exportedEntries);

  const tableIdToName = new Map<string, string>(dataModel.map((t) => [t.id, t.name] as const));
  return Object.entries(exportedFieldsByTable).flatMap(([tableId, exported]) => {
    const tableName = tableIdToName.get(tableId) ?? '';

    const triggerFilters: FilterRow[] = (exported.triggerObjectFields ?? []).map((field) => ({
      id: `${tableId}::trigger::${field}`,
      tableId,
      associatedObject: tableName,
      definition: tableName ? `${tableName}.${field}` : field,
      kind: 'trigger' as const,
      field,
    }));

    const ingestedFilters: FilterRow[] = (exported.ingestedDataFields ?? [])
      .filter((field): field is NonNullable<typeof field> => Boolean(field?.name))
      .map((field) => {
        const pathArr = Array.isArray(field.path) ? field.path : [];
        const pathStr = pathArr.join('→');
        return {
          id: `${tableId}::ingested::${pathStr}.${field.name}`,
          tableId,
          associatedObject: tableName,
          definition: `${tableName}→${pathStr}.${field.name}`,
          kind: 'ingested' as const,
          field: field.name,
          name: field.name,
          path: pathArr,
        };
      });

    return [...triggerFilters, ...ingestedFilters];
  });
};

const getAllowedTables = (filters: FilterRow[], dataModel: DataModel): string[] => {
  const MAX_FILTERS_PER_TABLE = 5;
  const filterCountByTableId = filters.reduce(
    (acc, filter) => {
      acc[filter.tableId] = (acc[filter.tableId] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  return dataModel.filter((table) => (filterCountByTableId[table.id] ?? 0) < MAX_FILTERS_PER_TABLE).map((t) => t.id);
};

const getTriggerFieldItems = (
  filters: FilterRow[],
  allowedTables: string[],
  dataModel: DataModel,
): TriggerFieldItem[] => {
  const existingFilterIds = new Set(filters.map(({ id }) => id));
  return dataModel
    .filter(({ id }) => allowedTables.includes(id))
    .flatMap((table) =>
      table.fields.map((field) => ({
        tableId: table.id,
        tableName: table.name,
        fieldName: field.name,
        label: `${table.name}.${field.name}`,
      })),
    )
    .filter((item) => !existingFilterIds.has(`${item.tableId}::trigger::${item.fieldName}`));
};

const getLinkedFieldItems = (pivots: Pivot[], allowedTables: string[], dataModel: DataModel): LinkPivotFieldItem[] =>
  pivots
    .filter((pivot) => pivot.type === 'link')
    .filter(({ pivotTableId }) => allowedTables.includes(pivotTableId))
    .flatMap(({ pivotTableId, baseTableId, pathLinks, baseTable }) => {
      const targetTable = dataModel.find(({ id }) => id === pivotTableId);
      if (!targetTable) return [];
      return targetTable.fields.map(({ name }) => ({
        baseTableId,
        pathLinks,
        fieldName: name,
        label: `${baseTable}→${pathLinks.join('→')}.${name}`,
      }));
    });

const filtersLoader = createServerFn()
  .middleware([authMiddleware])
  .handler(async function filtersLoader({ context }) {
    const { dataModelRepository } = context.authInfo;

    const dataModel = await dataModelRepository.getDataModel();

    const exportedEntries: [string, ExportedFields][] = await Promise.all(
      dataModel.map(async (table) => {
        const exported = await dataModelRepository.getDataModelTableExportedFields(table.id);
        return [table.id, exported] as const;
      }),
    );

    const filters = getFilters(exportedEntries, dataModel);
    const allowedTables = getAllowedTables(filters, dataModel);
    const triggerFieldItems = getTriggerFieldItems(filters, allowedTables, dataModel);
    const pivots = await dataModelRepository.listPivots({});
    const linkedFieldItems = getLinkedFieldItems(pivots, allowedTables, dataModel);

    return {
      filters,
      dataModel,
      pivots,
      allowedTables,
      triggerFieldItems,
      linkedFieldItems,
    };
  });

export const Route = createFileRoute('/_app/_builder/settings/analytics/filters')({
  loader: () => filtersLoader(),
  component: Filters,
});

const columnHelper = createColumnHelper<MarbleTableFeatures, FilterRow>();

function Filters() {
  const revalidate = useLoaderRevalidator();

  const { t } = useTranslation(['settings', 'common']);
  const { filters, dataModel, triggerFieldItems, linkedFieldItems } = Route.useLoaderData();

  const deleteFilterMutation = useDeleteFilterMutation();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<null | FilterRow>(null);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor((row) => row.associatedObject, {
        id: 'associatedObject',
        header: t('settings:filters.associated-object.row.header.label'),
        size: 240,
      }),
      columnHelper.accessor((row) => row.definition, {
        id: 'definition',
        header: t('settings:filters.definition.row.header.label'),
      }),
      columnHelper.display({
        id: 'actions',
        size: 80,
        cell: ({ row }) => {
          return (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                mode="icon"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={async (e) => {
                  e.stopPropagation();
                  setItemToDelete(row.original);
                  setIsConfirmOpen(true);
                }}
                aria-label="Delete filter"
                title="Delete filter"
              >
                <Icon icon="delete" className="size-4" />
              </Button>
            </div>
          );
        },
      }),
    ];
  }, [columnHelper, deleteFilterMutation]);

  const {
    table,
    getBodyProps,
    rows: tableRows,
    getContainerProps,
  } = useTable({
    data: filters,
    columns,
    columnResizeMode: 'onChange',

    enableSorting: false,
  });

  const handleDeleteFilter = () => {
    if (!itemToDelete) return;
    const payload: DeleteExportedFieldPayload =
      itemToDelete.kind === 'trigger'
        ? { triggerObjectField: itemToDelete.field! }
        : {
            ingestedDataField: {
              path: itemToDelete.path ?? [],
              name: itemToDelete.name!,
            },
          };
    deleteFilterMutation
      .mutateAsync({
        tableId: itemToDelete.tableId,
        payload,
      })
      .then(() => {
        setIsConfirmOpen(false);
        setItemToDelete(null);
        revalidate();
      })
      .catch(() => {
        toast.error(t('common:errors.unknown'));
      });
  };

  return (
    <Page.Content width="readable">
      <CollapsiblePaper.Container>
        <CollapsiblePaper.Title>
          <span className="flex-1">{t('settings:filters-settings')}</span>
          <CreateFilter
            dataModel={dataModel}
            triggerFieldItems={triggerFieldItems}
            linkedFieldItems={linkedFieldItems}
          />
        </CollapsiblePaper.Title>
        <CollapsiblePaper.Content className="flex flex-col h-full">
          <div className="flex flex-col gap-md flex-1 min-h-0">
            <Table.Container {...getContainerProps()} className="flex-1 min-h-0">
              <Table.Header headerGroups={table.getHeaderGroups()} />
              <Table.Body {...getBodyProps()}>
                {tableRows.map((row) => (
                  <Table.Row key={row.id} row={row} className="hover:bg-purple-background-light group" />
                ))}
              </Table.Body>
            </Table.Container>
          </div>
        </CollapsiblePaper.Content>
      </CollapsiblePaper.Container>
      <Modal.Root open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <Modal.Content size="medium">
          <Modal.Title>{t('settings:filters.delete_filter.title')}</Modal.Title>
          <Modal.Description className="p-lg text-left">
            {t('settings:filters.delete_filter.content')}
          </Modal.Description>
          <Modal.Footer>
            <Modal.FooterButton
              isCloseButton
              variant="secondary"
              label={t('common:cancel')}
              onClick={() => setIsConfirmOpen(false)}
            />
            <Modal.FooterButton
              variant="destructive"
              label={t('settings:filters.delete_filter')}
              onClick={handleDeleteFilter}
              leadingIcon="delete"
              isLoading={deleteFilterMutation.isPending}
            />
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </Page.Content>
  );
}
